import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { submitAppFeedback } from "@/lib/app-feedback";
import { StarRating } from "@/components/StarRating";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/lib/i18n";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/rate-app")({
  head: () => pageHead({
    path: "/rate-app",
    title: "Rate the app — Book Swap",
    description: "Tell us how Book Swap is working for you: give a rating and share what you liked and what we should improve.",
  }),
  component: RateApp,
});

function RateApp() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [pros, setPros] = useState("");
  const [cons, setCons] = useState("");
  const [general, setGeneral] = useState("");
  const [saving, setSaving] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return navigate({ to: "/login" });
    if (!rating && !pros.trim() && !cons.trim() && !general.trim()) {
      return toast.error(t("Please give a rating or share a comment."));
    }
    setSaving(true);
    const res = await submitAppFeedback(user.id, {
      rating: rating || null,
      pros: pros.trim() || null,
      cons: cons.trim() || null,
      general: general.trim() || null,
    });
    setSaving(false);
    if (!res.ok) return toast.error(t("Could not submit. Try again."));
    setRating(0); setPros(""); setCons(""); setGeneral("");
    toast.success(t("Thank you for your feedback! 💜"));
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="text-3xl font-bold">{t("Rate the app")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("This is about Book Swap itself — not about other readers. Your notes go straight to the team.")}
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-6 rounded-2xl bg-card p-6 shadow-[var(--shadow-card)]">
          <div>
            <Label className="text-base font-semibold">{t("Overall rating")}</Label>
            <div className="mt-2">
              <StarRating value={rating} onChange={setRating} size="lg" />
            </div>
          </div>

          <div>
            <Label htmlFor="pros">{t("What did you like?")}</Label>
            <Textarea id="pros" maxLength={1000} rows={3} value={pros} onChange={(e) => setPros(e.target.value)} placeholder={t("The parts that worked well for you…")} />
          </div>

          <div>
            <Label htmlFor="cons">{t("What could be improved?")}</Label>
            <Textarea id="cons" maxLength={1000} rows={3} value={cons} onChange={(e) => setCons(e.target.value)} placeholder={t("Anything confusing, slow, or missing…")} />
          </div>

          <div>
            <Label htmlFor="general">{t("Anything else?")}</Label>
            <Textarea id="general" maxLength={1000} rows={3} value={general} onChange={(e) => setGeneral(e.target.value)} placeholder={t("Optional")} />
          </div>

          {!user ? (
            <Button type="button" onClick={() => navigate({ to: "/login" })} className="w-full bg-primary hover:bg-primary/90">
              {t("Sign in to rate the app")}
            </Button>
          ) : (
            <Button type="submit" disabled={saving} className="w-full bg-primary hover:bg-primary/90">
              {saving ? t("Submitting…") : t("Send feedback")}
            </Button>
          )}
        </form>
      </div>
    </div>
  );
}
